// MobileThread — the touch thread: the root message on top, then its
// replies, then a composer that says who you are answering. Rendered as
// the layer in `ThreadLayer` on phones and as the pane on tablets.
//
// It always opens at the top, so the root lands where the eye followed
// it. Replies already read fold into one "N earlier replies" line and the
// list starts at the first unread reply, or at the last few when all are
// read, so what matters sits right under the root on one screen.

import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft } from "lucide-react";

import { FileUploadButton, UploadProgressRow } from "~/components/FileUpload";
import { useImageLightbox } from "~/components/ImageLightbox";
import { useMobileStatus } from "~/components/mobile/status";
import { mobileStrings } from "~/components/mobile/strings";
import { MessageBubble, type MessageGroup, type TimelineMessage } from "~/components/MessageBubble";
import { MessageComposer, type ComposerSubmitMeta } from "~/components/MessageComposer";
import { isMediaEvent, LongPressable, MessageMediaBody } from "~/components/Timeline";
import { useMessageMenu } from "~/components/mobile/MessageMenu";
import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { useToast } from "~/context/ToastContext";
import { useDraft } from "~/hooks/useDraft";
import { useFileUpload } from "~/hooks/useFileUpload";
import { useThread } from "~/hooks/useThread";
import { useThreadReadingPosition } from "~/hooks/useThreadReadingPosition";
import { useKeyboardInset } from "~/hooks/useVisualViewport";
import { sendThreadReadReceipt, type TimelineEvent } from "~/lib/matrix";
import type { QuoteComposerHandle } from "~/lib/quote";
import { computeGroupPositions, formatTimeOfDay, messageContentBody } from "~/lib/message-utils";
import { ICON_STROKE } from "~/lib/icon";

/** Shared view-transition name for the root message travelling between room and thread. */
export const THREAD_ROOT_TRANSITION = "thread-root";

/** How close to the end of the replies counts as "seen". */
const SEEN_SLACK_PX = 24;
/** Replies shown under the root when everything is read. */
const READ_TAIL = 3;
/** Folding fewer than this many replies saves nothing. */
const MIN_FOLD = 2;

/**
 * Index of the first reply to show. Everything before it is folded.
 * Read means up to this device's thread receipt or your own latest reply,
 * whichever is later.
 */
export function firstShownReply(
  replies: readonly Pick<TimelineEvent, "id" | "sender">[],
  readId: string | null,
  me: string | null,
): number {
  const receiptIndex = readId ? replies.findIndex((reply) => reply.id === readId) : -1;
  let ownIndex = -1;
  replies.forEach((reply, i) => {
    if (me && reply.sender.id === me) ownIndex = i;
  });
  const readIndex = Math.max(receiptIndex, ownIndex);
  const first =
    readIndex < replies.length - 1 ? readIndex + 1 : Math.max(0, replies.length - READ_TAIL);
  return first >= MIN_FOLD ? first : 0;
}

function toThreadMessage(
  event: TimelineEvent,
  roomId: string,
  group: MessageGroup,
  mediaBody?: ReactNode,
): TimelineMessage {
  return {
    id: event.id,
    roomId,
    sender: {
      id: event.sender.id,
      name: event.sender.displayName,
      avatarUrl: event.sender.avatarUrl,
    },
    time: formatTimeOfDay(event.ts),
    ts: event.ts,
    body: messageContentBody(event.content) ?? "",
    mediaBody,
    editedAt: event.editedAt,
    redacted: event.redacted,
    reactions: event.reactions,
    status: event.encryptionStatus,
    deliveryStatus: event.status,
    uploadProgress: event.uploadProgress,
    group,
    isThreadRoot: false,
  };
}

function ThreadHeader({
  titleId,
  roomName,
  onClose,
}: {
  titleId: string;
  roomName: string | null;
  onClose: () => void;
}) {
  const status = useMobileStatus();
  const line = status ?? (roomName ? mobileStrings.thread.inRoom(roomName) : null);
  return (
    <div className="grid h-14 shrink-0 grid-cols-[44px_1fr_44px] items-center px-2">
      {/* A thread is a level above its room, left like one: back at the
          leading edge, the same place and gesture as leaving a room. */}
      <button
        type="button"
        onClick={onClose}
        aria-label={mobileStrings.nav.back}
        className="flex size-11 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:text-foreground/90 focus-visible:shadow-neon-cyan"
      >
        <ChevronLeft size={20} strokeWidth={ICON_STROKE} />
      </button>
      <div className="flex min-w-0 flex-col items-center">
        <h2
          id={titleId}
          tabIndex={-1}
          className="truncate text-sm font-light text-foreground/90 outline-none"
        >
          {mobileStrings.thread.title}
        </h2>
        {line && (
          <span className="truncate text-[11px] font-light text-muted-foreground">{line}</span>
        )}
      </div>
      <span aria-hidden />
    </div>
  );
}

export function MobileThread({
  roomId,
  root,
  roomName = null,
  titleId,
  onClose,
}: {
  roomId: string;
  root: TimelineEvent;
  roomName?: string | null;
  /** Id for the visible title, so a surrounding dialog can name itself by it. */
  titleId?: string;
  onClose: () => void;
}) {
  const { client, userId, sendThreadReply } = useMatrix();
  const rooms = useMatrixRooms();
  const quoteComposerRef = useRef<QuoteComposerHandle | null>(null);
  const messageMenu = useMessageMenu({
    room: { id: roomId, isDirect: rooms.find((room) => room.id === roomId)?.isDirect ?? false },
    quoteComposerRef,
  });
  const { push: pushToast } = useToast();
  const { openImage, lightbox } = useImageLightbox();
  const {
    replies,
    latestReplyId,
    isLoading,
    isLoadingEarlier,
    earlierError,
    canLoadEarlier,
    loadEarlier,
    receiptsByEventId = new Map(),
  } = useThread(roomId, root.id, true, true);
  const draft = useDraft(roomId, root.id);
  const keyboardInset = useKeyboardInset();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useThreadReadingPosition(scrollRef, `${roomId}|${root.id}`, replies);
  const receiptSentFor = useRef<string | null>(null);
  const [foldBefore, setFoldBefore] = useState<string | null | undefined>(undefined);
  const headingId = titleId ?? `thread-title-${root.id}`;

  const media = (event: TimelineEvent) =>
    client && isMediaEvent(event) ? (
      <MessageMediaBody client={client} event={event} onOpenImage={openImage} />
    ) : undefined;
  const rootMessage = toThreadMessage(root, roomId, "single", media(root));

  const latestReplyIdRef = useRef(latestReplyId);
  useEffect(() => {
    latestReplyIdRef.current = latestReplyId;
  }, [latestReplyId]);

  const upload = useFileUpload(
    () => ({
      rootId: root.id,
      latestReplyId: latestReplyIdRef.current || root.id,
    }),
    () => scrollRef.current,
  );
  const onFiles = useCallback(
    (files: readonly File[]) => {
      void upload.uploadFiles(files, roomId);
    },
    [upload, roomId],
  );
  const activeUpload = upload.uploads.find(
    (record) => record.status === "uploading" || record.status === "sending",
  );
  const progressRows = upload.uploads
    .filter(
      (record) =>
        record.status === "failed" || (record.status === "uploading" && record !== activeUpload),
    )
    .map((record) => (
      <UploadProgressRow
        key={record.id}
        record={record}
        onCancel={() => upload.cancel(record.id)}
        onRetry={() => upload.retry(record.id, roomId)}
      />
    ));

  const markSeenIfAtEnd = useCallback(() => {
    const el = scrollRef.current;
    const latest = latestReplyIdRef.current;
    if (!el || !client || !latest || receiptSentFor.current === latest) return;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - SEEN_SLACK_PX) return;
    receiptSentFor.current = latest;
    void sendThreadReadReceipt(client, roomId, root.id, latest).catch((err) =>
      console.warn("sendThreadReadReceipt failed", err),
    );
  }, [client, roomId, root.id]);

  // Decide the fold once, on the first replies; later arrivals must not refold.
  if (foldBefore === undefined && replies.length > 0) {
    const readId = client?.bindings.getThreadReadReceipt(roomId, root.id) ?? null;
    const first = firstShownReply(replies, readId, userId);
    setFoldBefore(first > 0 ? (replies[first]?.id ?? null) : null);
  }
  const foldIndex = foldBefore ? replies.findIndex((reply) => reply.id === foldBefore) : -1;
  const folded = Math.max(0, foldIndex);
  // The first shown reply opens its own group, so it always carries a name.
  const groups = useMemo(
    () => [
      ...computeGroupPositions(replies.slice(0, folded)),
      ...computeGroupPositions(replies.slice(folded)),
    ],
    [replies, folded],
  );

  useEffect(() => {
    markSeenIfAtEnd();
  }, [replies, markSeenIfAtEnd]);

  const onSend = useCallback(
    async (
      body: string,
      _formattedBody: string | null,
      _mentions: string[],
      _mentionRoom: boolean,
      meta: ComposerSubmitMeta,
    ) => {
      await sendThreadReply(roomId, root.id, body, latestReplyIdRef.current);
      if (!meta.editedSinceSubmit()) draft.markSent();
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    },
    [sendThreadReply, roomId, root.id, draft],
  );

  const replyLabel =
    replies.length === 1
      ? mobileStrings.thread.oneReply
      : mobileStrings.thread.replies(replies.length);

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      style={{
        paddingBottom: keyboardInset > 0 ? keyboardInset : "var(--chat-phone-bottom-inset, 0px)",
      }}
    >
      <ThreadHeader titleId={headingId} roomName={roomName} onClose={onClose} />
      <div
        ref={scrollRef}
        data-send-list=""
        onScroll={markSeenIfAtEnd}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-2"
        style={{ overflowAnchor: "none" }}
      >
        <div data-thread-root style={{ viewTransitionName: THREAD_ROOT_TRANSITION }}>
          <LongPressable onLongPress={(anchor) => messageMenu.open(root, anchor)}>
            <MessageBubble mobile message={rootMessage} />
          </LongPressable>
        </div>
        <div className="flex items-center gap-3 px-4 pb-1 pt-5">
          <span className="text-[10px] uppercase tracking-widest text-muted-quiet">
            {isLoading && replies.length === 0
              ? mobileStrings.thread.loading
              : replies.length === 0
                ? mobileStrings.thread.noReplies
                : replyLabel}
          </span>
          <span aria-hidden className="h-px flex-1 bg-foreground/[0.06]" />
        </div>
        {(replies.length === 0 && (canLoadEarlier || isLoadingEarlier || earlierError)) ||
        folded > 0 ? (
          // One row for both controls: when the earlier page lands and its
          // control leaves, nothing below it moves.
          <div
            className="flex min-h-11 items-center gap-4"
            style={{
              paddingLeft:
                "calc(var(--chat-space-4) + var(--chat-timeline-avatar-column-comfortable) + var(--chat-space-3))",
              paddingRight: "var(--chat-space-4)",
            }}
          >
            {folded > 0 && (
              <button
                type="button"
                onClick={() => setFoldBefore(null)}
                className="chat-button flex min-h-11 items-center self-start text-left text-xs font-light text-muted-foreground outline-none transition-colors hover:text-foreground/90 focus-visible:shadow-neon-cyan"
              >
                {mobileStrings.thread.earlierReplies(folded)}
              </button>
            )}
            {(canLoadEarlier || isLoadingEarlier || earlierError) && (
              <button
                type="button"
                data-cy="thread-load-earlier"
                onClick={() => {
                  if (!earlierError) void loadEarlier();
                }}
                disabled={isLoadingEarlier || !canLoadEarlier}
                className="chat-button flex min-h-11 items-center self-start text-left text-xs font-light text-muted-foreground outline-none transition-colors hover:text-foreground/90 focus-visible:shadow-neon-cyan disabled:opacity-60"
              >
                {isLoadingEarlier
                  ? mobileStrings.thread.loadingEarlier
                  : earlierError
                    ? mobileStrings.thread.earlierError
                    : mobileStrings.thread.loadEarlier}
                {earlierError && (
                  <span data-cy="thread-earlier-error" className="ml-2 text-primary">
                    {mobileStrings.thread.retry}
                  </span>
                )}
              </button>
            )}
          </div>
        ) : null}
        {replies.map((reply, i) => (
          <div key={reply.localEchoId ?? reply.id} data-thread-reply={reply.id} hidden={i < folded}>
            <LongPressable onLongPress={(anchor) => messageMenu.open(reply, anchor)}>
              <MessageBubble
                mobile
                message={{
                  ...toThreadMessage(reply, roomId, groups[i] ?? "single", media(reply)),
                  receipts: receiptsByEventId.get(reply.id) ?? [],
                }}
              />
            </LongPressable>
          </div>
        ))}
      </div>
      <MessageComposer
        ref={quoteComposerRef}
        mobile
        client={client}
        placeholder={mobileStrings.thread.composerPlaceholder(root.sender.displayName)}
        onSend={onSend}
        value={draft.value}
        onValueChange={draft.setValue}
        attachButton={
          <FileUploadButton
            mobile
            onFiles={onFiles}
            onError={(message) => pushToast({ message, tone: "danger" })}
          />
        }
        progressRows={progressRows.length > 0 ? progressRows : undefined}
        onPasteFiles={onFiles}
      />
      {lightbox}
      {messageMenu.element}
    </div>
  );
}
